import { useEffect, useCallback, useRef } from 'react';
import api, { getNotifications } from '@/api';

/** Convert url-safe base64 VAPID key into a Uint8Array for pushManager.subscribe */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

export function usePushNotifications() {
  const subscribedRef = useRef(false);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return false;

    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;

    const result = await Notification.requestPermission();
    return result === 'granted';
  }, []);

  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      // Wait for SW to be ready/active so pushManager works
      await navigator.serviceWorker.ready;
      return registration;
    } catch (err) {
      console.error('SW registration failed:', err);
      return null;
    }
  }, []);

  const showLocalNotification = useCallback((title, body, options = {}) => {
    if (Notification.permission !== 'granted') return;

    const notif = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      ...options,
    });

    notif.onclick = () => {
      window.focus();
      notif.close();
    };

    return notif;
  }, []);

  /**
   * Full web-push subscription flow:
   *   1. Fetch VAPID public key from backend
   *   2. Register (or reuse) service worker
   *   3. pushManager.subscribe()
   *   4. POST subscription JSON to /api/push/subscribe
   */
  const subscribeToPush = useCallback(async () => {
    if (subscribedRef.current) return { success: true, cached: true };
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { success: false, reason: 'unsupported' };
    }
    if (Notification.permission !== 'granted') {
      return { success: false, reason: 'permission-denied' };
    }

    try {
      // 1. Fetch VAPID public key
      const res = await api.get('/push/vapid-public-key');
      const publicKey = res.data?.public_key;
      const enabled = res.data?.enabled;
      if (!publicKey || !enabled) {
        return { success: false, reason: 'vapid-not-configured' };
      }

      // 2. Ensure SW is registered and ready
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
      }
      await navigator.serviceWorker.ready;

      // 3. Subscribe (or reuse existing subscription)
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      // 4. Send to backend
      await api.post('/push/subscribe', subscription.toJSON());
      subscribedRef.current = true;
      return { success: true };
    } catch (err) {
      console.error('Push subscribe failed:', err);
      return { success: false, reason: 'subscribe-error', error: String(err) };
    }
  }, []);

  const unsubscribeFromPush = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      if (!registration) return { success: false };
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => {});
        await subscription.unsubscribe();
      }
      subscribedRef.current = false;
      return { success: true };
    } catch (err) {
      console.error('Unsubscribe failed:', err);
      return { success: false };
    }
  }, []);

  const sendTestPush = useCallback(async () => {
    try {
      const res = await api.post('/push/test');
      return res.data;
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }, []);

  // Poll for new notifications as an in-app fallback (badge counter, toast)
  const startPolling = useCallback(
    (intervalMs = 30000) => {
      let lastCount = 0;

      const check = async () => {
        try {
          const res = await getNotifications();
          const newCount = res.data.unread_count || 0;

          if (newCount > lastCount && lastCount > 0) {
            const latest = res.data.notifications?.[0];
            if (latest && !latest.read) {
              // Only fall back to local toast if server push isn't set up or SW not active
              if (!subscribedRef.current) {
                showLocalNotification(latest.title, latest.message);
              }
            }
          }
          lastCount = newCount;
        } catch (e) { /* ignore */ }
      };

      check();
      const id = setInterval(check, intervalMs);
      return () => clearInterval(id);
    },
    [showLocalNotification]
  );

  // Auto-register SW + auto-subscribe once on mount (only if user has already granted permission)
  useEffect(() => {
    registerServiceWorker().then(() => {
      if (Notification.permission === 'granted') {
        subscribeToPush();
      }
    });
  }, [registerServiceWorker, subscribeToPush]);

  return {
    requestPermission,
    showLocalNotification,
    startPolling,
    registerServiceWorker,
    subscribeToPush,
    unsubscribeFromPush,
    sendTestPush,
  };
}
